import { createFileMetadataFromUpload } from '../core/media/file-metadata.js';

/**
 * Maps a media picker `type` token (e.g. 'image' from `accept: 'image/*'`) to a
 * stored FileKind. Only known kinds pass through; anything else is ignored so
 * the list is not filtered by a bogus `kind`.
 *
 * @type {Record<string, import('../contracts/file-selection.js').FileKind>}
 */
const TYPE_TO_KIND = {
  image: 'image',
  pdf: 'pdf',
  archive: 'archive',
  document: 'document',
  spreadsheet: 'spreadsheet',
};

/**
 * Map a stored `/files` row to the narrow MediaItem shape the picker/tiptap use.
 *
 * @param {import('../contracts/file-selection.js').FileMeta} row
 * @returns {import('../contracts/media.js').MediaItem}
 */
function rowToMediaItem(row) {
  /** @type {import('../contracts/media.js').MediaItem} */
  const item = { url: row.url, name: row.name };
  if (row.thumbnail) item.thumbnail = row.thumbnail;
  if (row.mimeType) item.mimeType = row.mimeType;
  if (row.kind) item.kind = row.kind;
  if (row.extension) item.extension = row.extension;
  return item;
}

/**
 * Narrow REST media client. This is the ONLY file client passed globally as
 * `services.mediaClient` — the editor/tiptap path must not receive the broad
 * admin operations (those live in createRestFileManagerClient).
 *
 * @param {{ apiClient: import('../contracts/api.js').ApiClient }} deps
 * @returns {import('../contracts/media.js').MediaClient}
 */
export function createRestMediaClient({ apiClient }) {
  return {
    async list(query, type) {
      /** @type {Record<string, string>} */
      const params = { pageSize: '500' };
      if (typeof query === 'string' && query.trim() !== '') params.name = query.trim();
      const kind = type ? TYPE_TO_KIND[type] : undefined;
      if (kind) params.kind = kind;

      const result = /** @type {{ rows: import('../contracts/file-selection.js').FileMeta[], total: number }} */ (
        await apiClient.get('/files', { params })
      );
      return {
        items: result.rows.map(rowToMediaItem),
        total: result.total,
      };
    },

    async persistUpload(uploaded, file, opts) {
      const meta = createFileMetadataFromUpload(uploaded, file);
      const row = /** @type {import('../contracts/file-selection.js').FileMeta} */ (
        await apiClient.post('/files', meta, { signal: opts?.signal })
      );
      return rowToMediaItem(row);
    },
  };
}
